const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4200/api';

/**
 * Document type filter: 'ir' (IRs) or 'boarddeck' (Board Decks). Omit for both sections.
 * @typedef {'ir'|'boarddeck'} DocumentType
 */

/**
 * Fetch documents by company (grouped by section: IRs, Board Decks).
 * Uses IR_Ingested_UI for IRs and Board_Decks_Ingested_UI for Board Decks.
 * Each document includes name and path for viewing via getFileContentUrl.
 * @param {string} company - Company name (e.g. from URL companyId, decoded)
 * @param {DocumentType} [type] - Optional: 'ir' or 'boarddeck' to return only that section
 * @returns {Promise<{ sections: Array<{ title: string, documents: Array<{ name: string, path: string }> }> }>}
 */
export const fetchDocumentsByCompany = async (partnerCode, dealId, type) => {
  if (!partnerCode || !dealId) {
    throw new Error('partner_code and deal_id are required');
  }
  const params = new URLSearchParams({ partner_code: partnerCode.trim(),
    deal_id: dealId.trim(), });
  if (type === 'ir' || type === 'boarddeck') {
    params.set('type', type);
  }
  const url = `${API_BASE_URL}/documents?${params.toString()}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) {
    const errBody = await response.text();
    throw new Error(errBody || `HTTP ${response.status}`);
  }
  return response.json();
};

/**
 * Build URL to view/download a file via the backend file-content endpoint.
 * Use this URL in window.open or <a href> to view the file.
 * @param {string} path - Full file path (as returned in documents by company API)
 * @returns {string}
 */
export const getFileContentUrl = (path) => {
  if (!path || !path.trim()) return '';
  return `${API_BASE_URL}/documents/file-content?path=${encodeURIComponent(path.trim())}`;
};

/**
 * Upload a document for a company. Type must be 'ir' (IRs) or 'boarddeck' (Board Decks).
 * @param {string} company - Company name
 * @param {DocumentType} type - 'ir' or 'boarddeck'
 * @param {File} file - File to upload
 * @returns {Promise<{ success: boolean, path?: string }>}
 */
export const uploadDocument = async (partnerCode, dealId, type, file) => {
  if (!partnerCode || !dealId) throw new Error('partner_code and deal_id are required');
  if (type !== 'ir' && type !== 'boarddeck' && type !== 'activityreport') throw new Error('type must be ir or boarddeck or activityreport');
  if (!file || !(file instanceof File)) throw new Error('file is required');

  const form = new FormData();
  form.append('partner_code', String(partnerCode).trim());
  form.append('deal_id', String(dealId).trim());
  form.append('type', type);
  form.append('file', file, file.name);

  const response = await fetch(`${API_BASE_URL}/documents/upload`, {
    method: 'POST',
    body: form,
  });

  if (!response.ok) {
    const errBody = await response.text();
    let message = errBody || `HTTP ${response.status}`;
    try {
      const json = JSON.parse(errBody);
      if (json.error) message = json.error;
    } catch (_) {}
    throw new Error(message);
  }
  return response.json();
};

/**
 * Delete a document by path.
 * @param {string} path - The path of the document to delete
 * @returns {Promise<{ success: boolean }>}
 */
export const deleteDocument = async (path) => {
  if (!path || !path.trim()) throw new Error('path is required');

  const response = await fetch(`${API_BASE_URL}/documents/file?path=${encodeURIComponent(path.trim())}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const errBody = await response.text();
    let message = errBody || `HTTP ${response.status}`;
    try {
      const json = JSON.parse(errBody);
      if (json.error) message = json.error;
    } catch (_) {}
    throw new Error(message);
  }
  return response.json();
};

/**
 * Fetch activity report documents from Volume path.
 * @param {string} company - Company name
 * @param {string} [requestId] - Optional request ID to filter
 * @returns {Promise<Object>}
 */
export const fetchActivityReportDocuments = async (company, requestId) => {
  if (!company || !company.trim()) throw new Error('company is required');
  const params = new URLSearchParams({ company: company.trim() });
  if (requestId) params.set('request_id', requestId);

  const response = await fetch(`${API_BASE_URL}/documents/activity-reports?${params.toString()}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) {
    const errBody = await response.text();
    throw new Error(errBody || `HTTP ${response.status}`);
  }
  return response.json();
};

export const extractKpisFromFile = async (file) => {
  const form = new FormData();
  form.append("files", file);

  const response = await fetch(
    `${API_BASE_URL}/kpi-extractor/extract`,
    {
      method: "POST",
      body: form,
    }
  );

  if (!response.ok) {
    const err = await response.text();
    throw new Error(err || "KPI extraction failed");
  }

  return response.json();
};